import React, { useState } from 'react';
import { Reporter } from '@pf/shared';
import { useCreateContact } from '../hooks/useContacts';
import { useAddContactToList, usePressLists } from '../hooks/usePressLists';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? '').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

export interface AddToPressListModalProps {
  reporter: Reporter;
  onClose: () => void;
}

export function AddToPressListModal({ reporter, onClose }: AddToPressListModalProps) {
  const createContact = useCreateContact();
  const addToList = useAddContactToList();
  const { data, isLoading } = usePressLists();
  const lists = (data?.pressLists ?? []).filter((l) => !l.archivedAt);

  const [pressListId, setPressListId] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const alreadyInContacts = !!reporter.userContactId;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pressListId) return;
    setError('');
    setSubmitting(true);
    try {
      let contactId = reporter.userContactId ?? null;

      if (!contactId) {
        const nameParts = reporter.name.trim().split(/\s+/);
        const firstName = reporter.firstName ?? nameParts[0] ?? '';
        const lastName = (reporter.lastName ?? nameParts.slice(1).join(' ')) || firstName;
        const result = await createContact.mutateAsync({
          firstName,
          lastName,
          reporterId: reporter.id,
          email: reporter.email ?? undefined,
          title: reporter.title ?? undefined,
        });
        contactId = result.data.id;
      }

      await addToList.mutateAsync({ listId: pressListId, body: { contactId } });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to list');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add to Press List</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              {reporter.imageUrl ? (
                <img
                  src={reporter.imageUrl}
                  alt={reporter.name}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm flex items-center justify-center flex-shrink-0 select-none">
                  {getInitials(reporter.name)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{reporter.name}</p>
                {reporter.title && (
                  <p className="text-xs text-gray-500 truncate">{reporter.title}</p>
                )}
                {reporter.primaryPublication?.name && (
                  <p className="text-xs text-blue-600 truncate">{reporter.primaryPublication.name}</p>
                )}
              </div>
            </div>
            {!alreadyInContacts && (
              <p className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                This reporter will also be added to your contacts.
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select press list
            </label>
            <select
              value={pressListId}
              onChange={(e) => setPressListId(e.target.value)}
              disabled={isLoading}
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Choose a press list…</option>
              {lists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !pressListId}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Adding…' : 'Add to List'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
